import {
  FeatureName,
  Issue,
  IssueCreationSource,
  IssueMessageType,
  Message,
  useIsFeatureEnabled,
} from '@gripp/shared-logic';
import * as Haptics from 'expo-haptics';
import React, { useEffect, useRef, useState } from 'react';
import { Platform, StyleSheet, TouchableOpacity, View } from 'react-native';
import { Gesture, GestureDetector } from 'react-native-gesture-handler';
import { Text } from 'react-native-paper';
import { runOnJS, useSharedValue } from 'react-native-reanimated';
import { Colors } from '../../themes';
import { AccountAvatar } from '../accountAvatar';
import { colors as CropCircleColors } from '../crop-circle/tokens/colors';
import { LocationText } from '../locationText';
import {
  getIssueMessageHeader,
  getIssueMessageTextStyle,
} from './issueFormatUtils';
import { IssueMessageOld } from './issueMessage-old';
import { NewReaction } from './reaction/newReaction';
import { ReactionList } from './reaction/reactionList';
import { ReminderMessage } from './reminderMessage';
import { RoutineExceptionMessage } from './routineExceptionMessage';
import { RoutineMessage } from './routineMessage';
import { RoutinePromptMessage } from './routinePromptMessage';
import { MessageComponent } from './types';
import { UserMessage } from './userMessage';

export type IssueMessageProps = {
  message: Message;
  mediaUrl: string;
  maxImageWidth?: number;
  isUnread: boolean;
  issue: Issue;
  isFirstMessage: boolean;
  onImagePress?: (mediaId: string) => void;
};

const MessageComponents: Record<IssueMessageType, MessageComponent> = {
  [IssueMessageType.UserMessage]: UserMessage,
  [IssueMessageType.SystemMessage]: UserMessage,
  [IssueMessageType.SystemMessageReminderAlert]: UserMessage,
  [IssueMessageType.ReminderMessage]: ReminderMessage,
  [IssueMessageType.RoutineMessage]: RoutineMessage,
  [IssueMessageType.RoutinePromptMessage]: RoutinePromptMessage,
  [IssueMessageType.RoutineExceptionMessage]: RoutineExceptionMessage,
};

const UNREAD_HIGHLIGHT_TIME_MS = 3000;

// TODO: Crop Circle Redesign | remove wrapper once feature is fully rolled out
export const IssueMessage = (props: IssueMessageProps) => {
  const isCropCircleRedesignEnabled = useIsFeatureEnabled(
    FeatureName.CropCircleRedesign
  );

  if (!isCropCircleRedesignEnabled) {
    return <IssueMessageOld {...props} />;
  }

  return <IssueMessageBase {...props} />;
};

const IssueMessageBase = (props: IssueMessageProps) => {
  const MessageComponent = MessageComponents[props.message.type];
  const [reactionVisible, setReactionVisible] = useState(false);
  const anchorRef = useRef<View>(null);

  const maxImageWidth = props.maxImageWidth || 320;
  const imageDimensions = {
    width: maxImageWidth,
    height: maxImageWidth,
  };
  const message = props.message;
  const issue = props.issue;
  const header = getIssueMessageHeader(message);
  const textStyle = getIssueMessageTextStyle(message);

  const [unreadStyle, setUnreadStyle] = useState(props.isUnread);

  useEffect(() => {
    if (props.isUnread) {
      setTimeout(() => {
        setUnreadStyle(false);
      }, UNREAD_HIGHLIGHT_TIME_MS);
    }
  }, []);

  const isTouchingReactions = useSharedValue(false);

  const handleLongPress = () => {
    Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Medium);
    setReactionVisible(true);
  };

  const longPressGesture = Gesture.LongPress()
    .minDuration(500)
    .enabled(Platform.OS !== 'web')
    .onStart(() => {
      if (!isTouchingReactions.value) {
        runOnJS(handleLongPress)();
      }
      isTouchingReactions.value = false;
    });

  const showCreatedFrom =
    (issue.createdFrom === IssueCreationSource.Scanner ||
      issue.createdFrom === IssueCreationSource.Anonymousweb) &&
    props.isFirstMessage;

  const messageContent = (
    <View
      ref={anchorRef}
      style={[styles.container, unreadStyle ? styles.unread : styles.read]}
    >
      <View style={styles.titleRow}>
        <AccountAvatar
          avatar={header.avatar}
          avatarIcon={header.avatarIcon}
          mediaUrl={props.mediaUrl}
          size={32}
          style={header.avatarIcon ? header.avatarIconStyle : {}}
          iconColor={header.avatarIconColor}
          hasLightbox
        />
        <View style={styles.nameArea}>
          <Text style={[styles.name, header.fontStyle]} numberOfLines={1}>
            {header.updatedBy}
          </Text>
        </View>
        <Text style={styles.time}>{header.updatedAt}</Text>
      </View>
      {showCreatedFrom && (
        <LocationText
          location={issue.location}
          address={issue.createdFromAddress ?? ''}
        />
      )}
      <View style={styles.message}>
        <MessageComponent
          message={message}
          imageDimensions={imageDimensions}
          mediaUrl={props.mediaUrl}
          textStyle={textStyle}
          issue={props.issue}
          onImagePress={props.onImagePress}
        />
        {Platform.OS === 'android' ? (
          <View
            onTouchStart={() => {
              isTouchingReactions.value = true;
            }}
          >
            <ReactionList
              onOpenNewReaction={() => setReactionVisible(true)}
              issue={props.issue}
              message={message}
            />
          </View>
        ) : (
          <ReactionList
            onOpenNewReaction={() => setReactionVisible(true)}
            issue={props.issue}
            message={message}
          />
        )}
      </View>
    </View>
  );

  const newReaction = (
    <NewReaction
      onCloseReaction={() => setReactionVisible(false)}
      isReactionVisible={reactionVisible}
      issue={props.issue}
      message={message}
      anchorRef={anchorRef as React.RefObject<View>}
    />
  );

  if (Platform.OS === 'android') {
    return (
      <>
        <GestureDetector gesture={longPressGesture}>
          {messageContent}
        </GestureDetector>
        {newReaction}
      </>
    );
  }

  if (Platform.OS === 'ios') {
    return (
      <>
        <TouchableOpacity activeOpacity={1} onLongPress={handleLongPress}>
          {messageContent}
        </TouchableOpacity>
        {newReaction}
      </>
    );
  }

  return (
    <>
      {messageContent}
      {newReaction}
    </>
  );
};

const styles = StyleSheet.create({
  container: {
    flexDirection: 'column',
    padding: 16,
    gap: 8,
  },
  titleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    opacity: 0.8,
  },
  nameArea: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  name: {
    fontWeight: '700',
    fontSize: 14,
    lineHeight: 18,
    color: Colors.black,
  },
  time: {
    color: CropCircleColors.slate[400],
    fontSize: 12,
    fontWeight: '400',
    lineHeight: 16,
  },
  message: {
    marginTop: 0,
  },
  messageText: {
    flexWrap: 'wrap',
    fontSize: 14,
    fontWeight: '400',
    lineHeight: 22,
  },
  read: {
    backgroundColor: Colors.brand.white,
  },
  unread: {
    backgroundColor: Colors.unreadBackground,
  },
  modalContainer: {
    backgroundColor: 'transparent',
  },
  modalContentContainer: {
    marginTop: 'auto',
    height: 500,
  },
});
